import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

interface ItemsDisplayProps {
  items: Array<{
    id: string | number;
    type: string;
    order: number;
    name?: string;
  }>;
  maxWidth?: number;
}

const ItemsContainer = styled.div<{ maxWidth?: number }>`
  max-width: ${props => props.maxWidth || 200}px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: help;
`;

const Tooltip = styled.div<{ visible: boolean; x: number; y: number }>`
  position: fixed;
  left: ${props => props.x}px;
  top: ${props => props.y}px;
  background: #333;
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  z-index: 1000;
  max-width: 300px;
  white-space: pre-wrap;
  word-wrap: break-word;
  display: ${props => props.visible ? 'block' : 'none'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  
  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: 10px;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 5px solid #333;
  }
`;

const getItemTypeLabel = (type: string) => {
  switch (type) {
    case 'rawMaterial': return 'Matéria Prima';
    case 'service': return 'Serviço';
    case 'resource': return 'Insumo';
    case 'productFinal': return 'Produto Manufaturado';
    default: return type;
  }
};

const ItemsDisplay: React.FC<ItemsDisplayProps> = ({ items, maxWidth = 200 }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const itemsText = items.map(item => `${item.order}. ${item.name || 'Item sem nome'} (${getItemTypeLabel(item.type)})`).join('\n');

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const isOverflowing = containerRef.current.scrollWidth > containerRef.current.clientWidth;

      if (isOverflowing) {
        setTooltipPosition({
          x: e.clientX + 10,
          y: e.clientY - 10
        });
        setShowTooltip(true);
      }
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <>
      <ItemsContainer
        ref={containerRef}
        maxWidth={maxWidth}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {items.length > 0 ? items.map(item => `${item.order}. ${item.name || 'Item sem nome'} (${getItemTypeLabel(item.type)})`).join(', ') : 'Nenhum item'}
      </ItemsContainer>
      <Tooltip visible={showTooltip} x={tooltipPosition.x} y={tooltipPosition.y}>
        {itemsText}
      </Tooltip>
    </>
  );
};

export default ItemsDisplay; 